import { createSelector } from '@reduxjs/toolkit';

import { CURRENCY_SIGN } from '@/constants/countries';
import {
  getExtraBasicInfoQuestions,
  getTenantConfigs,
} from '@/constants/tenant-configs';

import { sortBankAccountsByProductAndCurrency } from '@/pages/account-type/utils/caymanAccount';

import { AccountTypeStatus } from '@/types/api/account';
import { PEPAnswerStatus, QuestionCode } from '@/types/api/pep';
import {
  DocumentSection,
  DocumentStatus,
  SigningDocument,
  SigningFormType,
} from '@/types/document';
import { ApplicationType, HolderType, UserAccountType } from '@/types/user';

import {
  getDocumentTypeStatus,
  getSignedDocuments,
  getSigningFormsAggregateStatus,
  hasValidAML4AOrAML4A_04_2025,
  hasValidAML6,
  standardizeDocumentsToSign,
} from '@/utils/document';
import { getSigningFormURL } from '@/utils/signingDocument';
import {
  getUserCurrency,
  hasUSFactor,
  isAdultUser,
  isRemediationCustomer,
} from '@/utils/user';

import {
  selectConfigsState,
  selectRightStartFeature,
} from '../configs/baseSelectors';
import { selectTerritories } from '../countries';
import { selectSigningDocumentsState } from '../documentSigning/selectors';
import { RootState } from '../store';

export const selectUserData = createSelector(
  (state: RootState) => state.userData,
  data => ({ ...data }),
);

export const selectUser = createSelector(selectUserData, data => data.user);

export const selectFinancialInfo = createSelector(
  selectUserData,
  data => data.financialInfo,
);

export const selectUserHolderJointMinor = createSelector(selectUserData, data =>
  (data.holders ?? []).find(
    holder => holder.holderType === HolderType.JointMinor,
  ),
);

export const selectUserHolderJointAdult = createSelector(selectUserData, data =>
  (data.holders ?? []).find(
    holder => holder.holderType === HolderType.JointAdult,
  ),
);

export const selectIsMinorAccount = createSelector(
  [selectUser, selectRightStartFeature],
  user => {
    return (
      user?.applicationType === ApplicationType.JOINT &&
      user?.holderType === HolderType.JointMinor
    );
  },
);

export const selectIsAdultAccount = createSelector(
  [selectUser, selectRightStartFeature],
  user => {
    return (
      user?.applicationType === ApplicationType.JOINT &&
      user?.holderType === HolderType.JointAdult
    );
  },
);

export const selectUserDataInitialized = createSelector(selectUserData, data =>
  Object.values(data.initialized).every(initialized => initialized),
);

export const selectAccountTypes = createSelector(selectUserData, user => {
  const { accountTypes } = user || {};
  return accountTypes || [];
});

export const selectUserTenant = createSelector(
  selectUser,
  user => user?.tenant,
);

export const selectSortedUserAccountTypes = createSelector(selectUser, user =>
  sortBankAccountsByProductAndCurrency(user?.accountTypes || []),
);

export const selectActiveUserAccountTypes = createSelector(
  selectSortedUserAccountTypes,
  accountTypes =>
    accountTypes.filter(type => type.status === AccountTypeStatus.Active),
);

export const selectSameOperationIdAccountTypes = createSelector(
  selectUser,
  user =>
    user?.accountTypes?.filter(
      type => type.operationId === user?.operationId,
    ) ?? [],
);

export const selectUserAccounts = createSelector(
  selectUser,
  data => data?.accountTypes || [],
);

export const selectUserAccountTypes = createSelector(selectUserData, data => {
  const { user, accountTypes } = data;
  return (user?.accountTypes || [])
    .map(({ id }, index) => {
      const foundAccountType = accountTypes?.find(type => type.id === id);
      if (foundAccountType) {
        return {
          ...foundAccountType,
          ...user?.accountTypes?.[index],
        };
      }
      return undefined;
    })
    .filter(i => !!i)
    .sort((a, b) => a.name.localeCompare(b.name));
});

export const selectAccountTypeById = createSelector(
  selectUserData,
  data => data.accountTypeById,
);

export const selectUserCurrency = createSelector(
  [selectUserData, selectConfigsState],
  ({ user, accountTypeById }, { defaultCurrency }) => {
    const currency = user
      ? getUserCurrency(user, { accountTypeById, defaultCurrency })
      : '';
    const currencyDisplay = CURRENCY_SIGN[currency] || currency;
    return { currency, currencyDisplay };
  },
);

export const selectSigningDocuments = createSelector(
  [
    selectConfigsState,
    selectUserData,
    selectSameOperationIdAccountTypes,
    selectUserAccountTypes,
    selectTerritories,
    selectRightStartFeature,
    selectSigningDocumentsState,
    selectFinancialInfo,
  ],
  (
    configData,
    userData,
    accountTypes,
    userAccountTypes,
    { territoryByCountryCode },
    rightStartEnabled,
    documentSigningState,
    financialInfo,
  ) => {
    const { user, documentsBySection, pepQuestions, rawRequiredSections } =
      userData;
    if (isRemediationCustomer(user) && !user?.isHighRisk) {
      return {
        formsToSign: [],
        documentsSigningStatus: DocumentStatus.Approved,
        signedDocuments: [],
        remainingDocsToSign: 0,
      };
    }

    const { signingDocs, hashSigningDocsByCode } = documentSigningState;

    let docToSign = [...signingDocs];
    let requiredSigningDocumentCodes: string[] = [];
    // TODO: Right Start -  Remove this when rolling out to all tenants
    if (rightStartEnabled) {
      const docSigningIdx = rawRequiredSections.findIndex(
        section => section.type === DocumentSection.DocumentSign,
      );
      requiredSigningDocumentCodes =
        rawRequiredSections[docSigningIdx]?.docs ?? [];
      docToSign = requiredSigningDocumentCodes.reduce((acc, doc) => {
        if (hashSigningDocsByCode[doc]) {
          acc.push(hashSigningDocsByCode[doc]);
        }
        return acc;
      }, [] as SigningDocument[]);
    }

    const { occupations, directorOccupations } = configData;
    const tenantConfigs = getTenantConfigs(user?.tenant);

    let formsToSign = standardizeDocumentsToSign(
      docToSign,
      accountTypes,
      user?.tenant ? tenantConfigs.formsOnEachAccountType : [],
    );

    const filteredFormsToSign = formsToSign.filter(item => {
      if (isRemediationCustomer(user)) {
        return item.docType === SigningFormType.AML10 && user?.isHighRisk;
      }
      if (!user?.isHighRisk && item.docType === SigningFormType.AML10) {
        return false;
      }

      if (item.docType === SigningFormType.W9) {
        return hasUSFactor(user);
      }

      // When AML4A_06_2025 is required but user has already submitted AML4A/AML4A_04_2025 and AML6:
      // - If both AML4A/AML4A_04_2025 and AML6 is In Review or Approved, hide AML4A_06_2025
      // - Any other case, hide AML4A/AML4A_04_2025 and AML6, show AML4A_06_2025
      if (item.docType === SigningFormType.AML4A_06_2025) {
        return !(hasValidAML4AOrAML4A_04_2025(user) && hasValidAML6(user));
      }

      return true;
    });

    formsToSign = filteredFormsToSign.map(item => ({
      ...item,
      url: getSigningFormURL({
        docToSign: item,
        occupations,
        directorOccupations,
        user,
        userAccountTypes: userAccountTypes as UserAccountType[],
        pepQuestions,
        territoryByCountryCode,
        financialInfo,
      }),
    }));

    const isRequiredAML4A_06_2025 = formsToSign.some(
      item => item.docType === SigningFormType.AML4A_06_2025,
    );

    const allSubmittedSigningDocuments =
      documentsBySection[DocumentSection.DocumentSign] || [];

    // When AML4A_06_2025 is required but user has already submitted AML4A/AML4A_04_2025 and AML6:
    // - If both AML4A/AML4A_04_2025 and AML6 is In Review or Approved, shows them
    // - Any other case, hide them
    const filteredSigningDocs = allSubmittedSigningDocuments.filter(doc => {
      if (
        isRequiredAML4A_06_2025 &&
        [
          SigningFormType.AML4A_04_2025,
          SigningFormType.AML4A,
          SigningFormType.AML6,
        ].includes(doc.docType as SigningFormType)
      ) {
        return hasValidAML4AOrAML4A_04_2025(user) && hasValidAML6(user);
      }
      return true;
    });

    const shouldAutoCompleteDocumentSigning =
      tenantConfigs.autoCompleteAdultWithNoDocumentSigning &&
      isAdultUser(user) &&
      requiredSigningDocumentCodes.length === 0;

    const documentsSigningStatus = shouldAutoCompleteDocumentSigning
      ? DocumentStatus.Completed
      : getSigningFormsAggregateStatus(filteredSigningDocs, formsToSign.length);

    const signedDocuments = getSignedDocuments(
      filteredSigningDocs,
      formsToSign,
    );

    return {
      formsToSign,
      documentsSigningStatus,
      signedDocuments,
      remainingDocsToSign: Math.max(
        formsToSign.length - signedDocuments.length,
        0,
      ),
    };
  },
);

export const selectLivenessStatuses = createSelector(selectUserData, data => {
  const { documentsBySection } = data;

  const selfieStatus = getDocumentTypeStatus(
    documentsBySection[DocumentSection.Selfie] || [],
  );

  const livenessStatus = getDocumentTypeStatus(
    documentsBySection[DocumentSection.IDVideo] || [],
  );

  return {
    selfieStatus,
    livenessStatus,
  };
});

export const selectPEPQuestions = createSelector(
  selectUserData,
  data => data.pepQuestions,
);

export const selectPersonalUseQuestion = createSelector(selectUserData, data =>
  data.pepQuestions.find(q => q.code === QuestionCode.Personal),
);

export const selectEXCUserReviewStatus = createSelector(
  selectUser,
  user => !!user?.clientReviewed,
);

export const selectBasicInfoStatus = createSelector(selectUserData, data => {
  const { user } = data;

  if (!user) return undefined;

  const extraQuestionsLeft = Object.values(
    getExtraBasicInfoQuestions(user.tenant, user),
  ).filter(item => item.enabled);

  if (extraQuestionsLeft.length > 0) return undefined;

  let status: DocumentStatus | undefined = undefined;

  switch (user.pepAnswerStatus) {
    case PEPAnswerStatus.Approved:
      status = DocumentStatus.Approved;
      break;
    case PEPAnswerStatus.Rejected:
      status = DocumentStatus.Rejected;
      break;
    case PEPAnswerStatus.InReview:
      status = DocumentStatus.InReview;
      break;
  }

  return status;
});
